import { AxiosError, AxiosResponse } from 'axios';

import { api } from './index';
import { CommonResType } from '@/types/commonApiType';
import { useTokenStore } from '@/store/tokenStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';

// 아이디 중복 확인(가입시)
type CheckIdDuplicateDataType = {
	member_id: string;
};
type CheckIdDuplicateResType = CommonResType;
export const useCheckIdDuplicateApi = () => {
	const url = '/v1/front/checkDuplicateId';
	return useMutation<AxiosResponse<CheckIdDuplicateResType>, AxiosError, CheckIdDuplicateDataType>({
		mutationFn: (data: CheckIdDuplicateDataType) => api.post(url, data),
	});
};
type RequestTokenDataType = {
	refresh_token: string;
};
type RequestTokenResType = CommonResType;
export const useRequestTokenApi = () => {
	const url = '/v1/front/requestToken';
	return useMutation<AxiosResponse<RequestTokenResType>, AxiosError, RequestTokenDataType>({
		mutationFn: (data: RequestTokenDataType) => api.post(url, data),
	});
};

// 회원가입 제외-인증번호 확인
type CheckIdentificationNumbeDataType = {
	phonenum: string;
	identification_no: string;
};
type CheckIdentificationNumbeResType = CommonResType;
export const useCheckIdentificationNumberApi = () => {
	const url = '/v1/front/checkIdentificationNumber';
	return useMutation<AxiosResponse<CheckIdentificationNumbeResType>, AxiosError, CheckIdentificationNumbeDataType>({
		mutationFn: (data: CheckCertificationDataType) => api.post(url, data),
	});
};

// 회원가입-인증번호 확인
type CheckCertificationDataType = {
	phonenum: string;
	identification_no: string;
};
type CheckCertificationResType = CommonResType;
export const useCheckCertificationApi = () => {
	const url = '/v1/front/checkIdentificationNumberForJoin';
	return useMutation<AxiosResponse<CheckCertificationResType>, AxiosError, CheckCertificationDataType>({
		mutationFn: (data: CheckCertificationDataType) => api.post(url, data),
	});
};

// 회원가입-추천인 코드 확인
type CheckRecommendCodeDataType = {
	recommend_code: string;
};
type CheckRecommendCodeResType = CommonResType;
export const useCheckRecommendCodeApi = () => {
	const url = '/v1/front/checkRecommendCode';
	return useMutation<AxiosResponse<CheckRecommendCodeResType>, AxiosError, CheckRecommendCodeDataType>({
		mutationFn: (data: CheckRecommendCodeDataType) => api.post(url, data),
	});
};
// 일반 회원가입
export type SignupDataType = {
	member_name: string;
	member_en_name: string;
	member_birth_year: string;
	member_birth_month: string;
	member_birth_date: string;
	member_gender: string;
	member_cell: string;
	member_id: string;
	member_password: string;
	member_re_password: string;
	member_zipcode: string;
	member_address: string;
	member_address_detail: string;
	member_email: string;
	member_company_name: string;
	member_company_en_name: string;
	member_position: string;
	member_md: string;
	member_company_tel: string;
	member_company_fax: string;
	member_company_zipcode: string;
	member_company_address: string;
	member_company_address_detail: string;
	member_edu: string;
	member_research_topic: string;
	member_receive_post: string;
};

export const usePostSignupApi = () => {
	const url = '/v1/front/signup';
	return useMutation<AxiosResponse<CommonResType>, AxiosError, SignupDataType>({
		mutationFn: (data: SignupDataType) => api.post(url, data),
	});
};
export type CheckJoinDuplicateDataType = { param: string; param_type: string };
export const usePostCheckJoinDuplicateApi = () => {
	const url = '/v1/front/checkJoinDuplicate';
	return useMutation<AxiosResponse<CommonResType>, AxiosError, CheckJoinDuplicateDataType>({
		mutationFn: (data: CheckJoinDuplicateDataType) => api.post(url, data),
	});
};
export type SendEmailVerificationDataType = { email: string };
export const usePostSendEmailVerificationApi = () => {
	const url = '/v1/front/sendEmailVerification';
	return useMutation<AxiosResponse<CommonResType>, AxiosError, SendEmailVerificationDataType>({
		mutationFn: (data: SendEmailVerificationDataType) => api.post(url, data),
	});
};

export type VerifyCodeDataType = { email: string; code: string };
export const usePostVerifyCodeApi = () => {
	const url = '/v1/front/verifyCode';
	return useMutation<AxiosResponse<CommonResType>, AxiosError, VerifyCodeDataType>({
		mutationFn: (data: VerifyCodeDataType) => api.post(url, data),
	});
};
// 비밀번호 찾기
type FindMemeberPasswordDataType = {
	member_email: string;
	member_id: string;
};
type FindMemeberPasswordResType = CommonResType;
export const useFindMemeberPasswordApi = () => {
	const url = '/v1/front/findMemberPassword';
	return useMutation<AxiosResponse<FindMemeberPasswordResType>, AxiosError, FindMemeberPasswordDataType>({
		mutationFn: (data: FindMemeberPasswordDataType) => api.post(url, data),
	});
};

// 비밀번호 변경
type ResetMemberPasswordDataType = { member_id: string; member_password: string };
type ResetMemberPasswordResType = CommonResType;
export const usePostResetPwApi = () => {
	const url = '/v1/front/resetPw';
	return useMutation<AxiosResponse<ResetMemberPasswordResType>, AxiosError, ResetMemberPasswordDataType>({
		mutationFn: (data: ResetMemberPasswordDataType) => api.post(url, data),
	});
};
// 비밀번호 변경(마이페이지지)
type ChangePwDataType = { member_original_password: string; member_new_password: string };
export const usePostChangePwApi = () => {
	const url = '/v1/front/changePw';
	return useMutation<AxiosResponse<CommonResType>, AxiosError, ChangePwDataType>({
		mutationFn: (data: ChangePwDataType) => api.post(url, data),
	});
};

// 로그인
type LoginDataType = {
	member_id: string;
	member_password: string;
};
type LoginResType = CommonResType<{
	member_pk: string;
	member_name: string;
	access_token: string;
	refresh_token: string;
}>;
export const useLoginApi = () => {
	const url = '/v1/front/login';
	const queryClient = useQueryClient();
	const tokenStore = useTokenStore();
	const authStore = useAuthStore();
	return useMutation<AxiosResponse<LoginResType>, AxiosError, LoginDataType>({
		mutationFn: (data: LoginDataType) => api.post(url, data),
		onSuccess: (res) => {
			if (res.data.success) {
				queryClient.clear(); // 로그인시 이전의 모든 캐시 삭제
				authStore.setName(res.data.data.member_name); // 로그인시 이름 저장
				authStore.setPk(res.data.data.member_pk); // 로그인시 pk 저장
				tokenStore.setAccessToken(res.data.data.access_token); // 토큰 저장
				tokenStore.setRefreshToken(res.data.data.refresh_token);
			}
		},
	});
};
// 로그아웃
export const useLogoutApi = () => {
	const url = '/v1/front/logout';
	const queryClient = useQueryClient();
	const tokenStore = useTokenStore();
	const authStore = useAuthStore();
	return useMutation<AxiosResponse, AxiosError, void>({
		mutationFn: () => api.post(url),
		onSuccess: (res) => {
			if (res.data.success) {
				window.location.href = '/'; // 로그아웃시 메인페이지로 이동
				queryClient.clear(); // 로그인시 이전의 모든 캐시 삭제
				authStore.clearValues();
				tokenStore.resetToken();
			}
		},
	});
};

// 소셜 로그인 검증
type RequestTSignParamType = {
	// COMMENT : 소셜 로그인 소통하면서 변경할 예정입니다
	token: string;
	device_type?: string;
};
type RequestTSignResType = {
	success: true;
	data: {
		member_pk: number;
		member_name: string;
		member_push_yn: string;
		access_token: string;
		refresh_token: string;
		member_nickname: string;
		basic_info_yn: boolean;
		member_join_path: string;
		group_pk: number | null;
		group_sp: number | null;
	};
};

export const useRequestTSignApi = () => {
	const url = '/v1/front/requestTSign';
	return useMutation<AxiosResponse<RequestTSignResType>, AxiosError, RequestTSignParamType>({
		mutationFn: (data: RequestTSignParamType) => api.post(url, data),
	});
};

// 아이디 찾기
type FindMemeberIdDataType = {
	member_name: string;
	member_email: string;
};
type FindMemeberIdResType = CommonResType<{
	member_id: string;
}>;
export const useFindMemeberIdApi = () => {
	const url = '/v1/front/findMemberId';
	return useMutation<AxiosResponse<FindMemeberIdResType>, AxiosError, FindMemeberIdDataType>({
		mutationFn: (data: FindMemeberIdDataType) => api.post(url, data),
	});
};

type GetCheckTokenResType = CommonResType;
export const useGetCheckTokenApi = () => {
	const url = '/v1/front/checkToken';
	return useQuery<AxiosResponse, AxiosError, GetCheckTokenResType>({
		queryKey: [url],
		queryFn: () => api.get(url),
		select: (res) => res.data,
		enabled: false,
	});
};
